import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  CAMPAIGN_CATEGORIES,
  CAMPAIGN_CATEGORY_LABELS,
  CAMPAIGN_JOIN_MODES,
  CAMPAIGN_TRANSITIONS,
  PLATFORM_LABELS,
  PLATFORMS,
  SOURCE_LINK_KINDS,
  SOURCE_LINK_KIND_LABELS,
  formatCents,
  guessSourceLinkKind,
  type AdminCampaign,
  type AdminCampaignStats,
  type CampaignApplicant,
  type CampaignJoinMode,
  type CampaignStatus,
  type Platform,
  type PlatformRates,
  type SourceLink,
  type SourceLinkKind,
} from '@nexus/shared';

import { ApiError, apiGet, apiSend } from '../../lib/api';
import { ClipFolderPicker } from '../CampaignLibraryAdmin';
import {
  Alert,
  Badge,
  Button,
  Card,
  CheckboxField,
  DataTable,
  EmptyState,
  ExternalLink,
  Fact,
  Facts,
  FieldGroup,
  FieldRow,
  Glyph,
  LoadingPage,
  Money,
  Num,
  PageHeader,
  SelectField,
  Stat,
  StatGrid,
  MoneyField,
  StatusPill,
  centsToDollarInput,
  parseDollars,
  parseOptionalDollars,
  TextField,
  When,
  statusLabel,
  type Column,
} from '../../ui';

/**
 * The operator's campaign screens.
 *
 * ## The create form is grouped, not listed
 *
 * Seventeen inputs in a column is a form nobody reads to the bottom. They are
 * four decisions — what and for whom, what it pays, what a clip must be, and
 * where the material is — and the fieldsets say so.
 *
 * ## Money is entered in dollars and stored in cents
 *
 * Every amount in this system is integer cents, and the form used to ask for
 * cents directly, which is how `$5,000` gets typed as `5000` and funds a
 * campaign with fifty dollars. `MoneyField` takes dollars, prints the exact
 * integer it is about to send under the box as it is typed, and refuses
 * anything it cannot read instead of defaulting it — see `ui/money.ts` for why
 * `Number(text) * 100` is three different silent losses.
 */

interface CampaignListRow extends AdminCampaign {
  creatorBudgetFundedCents: number;
}

/**
 * How the list can be ordered.
 *
 * The operator asked to see "how many clippers joined each campaign", and a
 * column nobody can order by is a column you read fifteen times. Joined and
 * spend are the two the question is actually about: who is here, and what it
 * is costing.
 */
const SORTS = ['newest', 'joined', 'spend', 'views'] as const;
type CampaignSort = (typeof SORTS)[number];

const SORT_LABELS: Record<CampaignSort, string> = {
  newest: 'Newest first',
  joined: 'Most creators joined',
  spend: 'Most spent',
  views: 'Most verified views',
};

export function AdminCampaigns(): React.JSX.Element {
  const [rows, setRows] = useState<CampaignListRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /* IN THE URL, like every other sort in this app: a reload keeps it, the back
     button works, and an operator can send somebody the exact view they are
     looking at. */
  const [params, setParams] = useSearchParams();
  const raw = params.get('sort') ?? '';
  const sort: CampaignSort = (SORTS as readonly string[]).includes(raw)
    ? (raw as CampaignSort)
    : 'newest';

  const load = useCallback(async () => {
    try {
      const body = await apiGet<{ campaigns: CampaignListRow[] }>('/admin/campaigns');
      setRows(body.campaigns);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load campaigns.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* Sorted HERE and not at the API: the operator's list is every campaign there
     is, it arrives in one response, and a server-side sort would be a second
     place for the order to be decided. The day this list is paged the sort goes
     with the page. */
  const sorted = useMemo(() => {
    const all = [...(rows ?? [])];
    if (sort === 'newest') return all;
    return all.sort((a, b) =>
      sort === 'joined'
        ? b.stats.creatorsJoined - a.stats.creatorsJoined
        : sort === 'views'
          ? b.stats.verifiedViews - a.stats.verifiedViews
          : b.stats.spendCents - a.stats.spendCents,
    );
  }, [rows, sort]);

  if (rows === null && !error) return <LoadingPage eyebrow="Operator" title="Campaigns" stats />;

  const live = (rows ?? []).filter((r) => r.status === 'ACTIVE');
  const committed = (rows ?? []).reduce((sum, r) => sum + r.budgetCents, 0);
  const funded = (rows ?? []).reduce((sum, r) => sum + r.creatorBudgetFundedCents, 0);
  const joined = (rows ?? []).reduce((sum, r) => sum + r.stats.creatorsJoined, 0);
  const clips = (rows ?? []).reduce((sum, r) => sum + r.stats.clips.total, 0);

  const columns: Array<Column<CampaignListRow>> = [
    {
      key: 'name',
      header: 'Campaign',
      primary: true,
      cell: (row) => (
        <>
          <Link to={`/admin/campaigns/${row.slug}`}>{row.name}</Link>
          <span className="cell-note">{row.brand.displayName}</span>
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => <StatusPill status={row.status} domain="campaign" />,
    },
    {
      key: 'joined',
      header: 'Creators joined',
      numeric: true,
      align: 'end',
      cell: (row) => (
        <>
          <Num value={row.stats.creatorsJoined} />
          {/* Joined and ACTIVE are different questions — somebody can accept
              the terms and never post — and the gap between them is the one
              number that says whether a campaign is working. */}
          <span className="cell-note">{row.stats.creatorsActive} posting</span>
        </>
      ),
    },
    {
      key: 'clips',
      header: 'Clips',
      numeric: true,
      align: 'end',
      cell: (row) => (
        <>
          <Num value={row.stats.clips.total} />
          <span className="cell-note">{clipBreakdown(row.stats.clips)}</span>
        </>
      ),
    },
    {
      key: 'views',
      header: 'Verified views',
      numeric: true,
      align: 'end',
      cell: (row) => <Num value={row.stats.verifiedViews} />,
    },
    {
      key: 'spend',
      header: 'Spend',
      numeric: true,
      align: 'end',
      cell: (row) => (
        <>
          <Money cents={row.stats.spendCents} />
          <span className="cell-note">{formatCents(row.stats.marginCents)} kept</span>
        </>
      ),
    },
    {
      key: 'remaining',
      header: 'Budget left',
      numeric: true,
      align: 'end',
      cell: (row) => (
        <>
          <Money cents={row.stats.budgetRemainingCents} />
          {row.creatorBudgetFundedCents < row.budgetCents && (
            <span className="cell-note tone-waiting">
              {formatCents(row.budgetCents - row.creatorBudgetFundedCents)} not paid in
            </span>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Operator"
        title="Campaigns"
        lead="A campaign is created as a draft, funded, then made live. Creators accept its terms the moment they join, so what they accepted is frozen."
        actions={
          <Button variant={creating ? 'ghost' : 'primary'} onClick={() => setCreating((v) => !v)}>
            {creating ? 'Cancel' : 'New campaign'}
          </Button>
        }
      />

      {error && <Alert tone="critical">{error}</Alert>}

      <StatGrid dense>
        <Stat label="Campaigns" value={<Num value={(rows ?? []).length} />} />
        <Stat label="Live" value={<Num value={live.length} />} emphasis={live.length > 0} />
        <Stat label="Creators joined" value={<Num value={joined} />} note="Across every campaign." />
        <Stat label="Clips" value={<Num value={clips} />} />
        <Stat label="Budget committed" value={<Money cents={committed} />} />
        <Stat
          label="Funded"
          value={<Money cents={funded} />}
          note={funded < committed ? 'Some budget is not yet paid in.' : 'Every campaign is paid in.'}
        />
      </StatGrid>

      {creating && (
        <CreateCampaign
          onCreated={() => {
            setCreating(false);
            void load();
          }}
        />
      )}

      <Card
        title="Every campaign"
        sub="Creators joined, clips by status, verified views and what the ledger says each one has cost."
        aside={
          <SelectField
            label="Sort by"
            name="sort"
            value={sort}
            onChange={(event) => {
              const next = new URLSearchParams(params);
              next.set('sort', event.target.value);
              setParams(next);
            }}
          >
            {SORTS.map((value) => (
              <option key={value} value={value}>
                {SORT_LABELS[value]}
              </option>
            ))}
          </SelectField>
        }
      >
        <DataTable
          columns={columns}
          rows={sorted}
          getKey={(row) => row.id}
          empty={
            <EmptyState
              title="No campaigns yet."
              action={
                <Button variant="primary" onClick={() => setCreating(true)}>
                  Create the first one
                </Button>
              }
            >
              A campaign needs a client, a rate and a budget. It is created as a draft and nothing
              is visible to creators until it is live.
            </EmptyState>
          }
        />
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Create                                                              */
/* ------------------------------------------------------------------ */

interface BrandRow {
  id: string;
  displayName: string;
}

function CreateCampaign({ onCreated }: { onCreated: () => void }): React.JSX.Element {
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newBrand, setNewBrand] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const body = await apiGet<{ brands: BrandRow[] }>('/admin/brands');
        setBrands(body.brands);
      } catch {
        setError('Could not load clients.');
      }
    })();
  }, []);

  async function addBrand(): Promise<void> {
    const name = newBrand.trim();
    if (!name) return;
    try {
      const body = await apiSend<{ brand: BrandRow }>('POST', '/admin/brands', {
        legalName: name,
        displayName: name,
      });
      setBrands((prev) => [...prev, body.brand]);
      setNewBrand('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the client.');
    }
  }

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await apiSend('POST', '/admin/campaigns', {
        brandId: String(form.get('brandId')),
        name: String(form.get('name')),
        slug: String(form.get('slug')),
        objective: emptyToNull(form.get('objective')),
        category: String(form.get('category')),
        joinMode: joinModeOf(form.get('joinMode')),
        ratePerThousandCents: dollarsToCents('Rate', form.get('ratePerThousand')),
        platformRates: platformRatesOf(form),
        budgetCents: dollarsToCents('Budget', form.get('budget')),
        perCreatorCapCents: dollarsToCentsOrNull('Per-creator cap', form.get('perCreatorCap')),
        perSubmissionCapCents: dollarsToCentsOrNull('Per-clip cap', form.get('perSubmissionCap')),
        minAccountAgeDays: wholeNumber('Minimum account age', form.get('minAccountAgeDays')),
        minFollowers: wholeNumber('Minimum followers', form.get('minFollowers')),
        allowedPlatforms: PLATFORMS.filter((p) => form.get(`platform-${p}`) === 'on'),
        startsAt: emptyToNull(form.get('startsAt')),
        endsAt: emptyToNull(form.get('endsAt')),
        contentRules: emptyToNull(form.get('contentRules')),
        termsText: emptyToNull(form.get('termsText')),
        sourceLinks: readSourceLinks(form),
      });
      onCreated();
    } catch (err) {
      setError(refusal(err) ?? 'Could not create the campaign.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      variant="feature"
      title="New campaign"
      sub="Created as a draft. Record the funding, then move it to funded and live from the campaign's own page."
    >
      <form onSubmit={submit} className="form">
        <FieldGroup legend="Basics" note="Who it is for, and what it is called.">
          <SelectField label="Client" name="brandId" required defaultValue="">
            <option value="" disabled>
              Choose a client
            </option>
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.displayName}
              </option>
            ))}
          </SelectField>

          <FieldRow>
            <TextField
              label="New client"
              type="text"
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              placeholder="Jag"
              hint="Adds a client without leaving this form."
            />
            <div className="field field-button">
              <Button onClick={() => void addBrand()}>Add client</Button>
            </div>
          </FieldRow>

          <FieldRow>
            <TextField label="Name" type="text" name="name" required />
            <TextField
              label="Slug"
              type="text"
              name="slug"
              required
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              hint="Lower case and hyphens. It is the address creators see."
            />
          </FieldRow>

          <FieldRow>
            <TextField
              label="Objective"
              name="objective"
              textarea
              rows={2}
              hint="One line, shown on the creator's card."
            />
            {/**
             * THE NICHE, REQUIRED, DEFAULTED TO `other`.
             *
             * It is how a creator finds the campaign: the Campaigns page is a
             * search box and a row of category pills, and a campaign filed under
             * `other` is reachable only by name. Required so the question is
             * asked, defaulted so it can never block a campaign at 1am when the
             * client's niche is not on the list.
             */}
            <SelectField
              label="Category"
              name="category"
              required
              defaultValue="other"
              hint="The niche creators filter by on the Campaigns page."
            >
              {CAMPAIGN_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {CAMPAIGN_CATEGORY_LABELS[category]}
                </option>
              ))}
            </SelectField>
          </FieldRow>
          <JoinModeField defaultValue="OPEN" />
        </FieldGroup>

        <FieldGroup legend="Economics" note="Entered in dollars, stored as integer cents.">
          <FieldRow>
            <MoneyField
              label="Creator rate per 1,000 views"
              name="ratePerThousand"
              required
              defaultValue="0.25"
              hint="What a creator earns per 1,000 verified views, on any platform without its own rate below."
            />
            <MoneyField
              label="Budget"
              name="budget"
              required
              defaultValue="0"
              hint="The whole campaign's creator-facing budget — what every creator on it can earn between them."
            />
          </FieldRow>
          <PlatformRateFields rates={{}} />
          <FieldRow>
            <MoneyField
              label="Per-creator cap"
              name="perCreatorCap"
              optional
              blankMeans="Blank: no cap — one creator could earn the whole budget."
              hint="The most any single creator can earn here."
            />
            <MoneyField
              label="Per-clip cap"
              name="perSubmissionCap"
              optional
              blankMeans="Blank: no cap — one clip could earn the whole budget."
              hint="The most any single clip can earn, whatever it reaches."
            />
          </FieldRow>
        </FieldGroup>

        <FieldGroup
          legend="Eligibility"
          note="Who may take it. A creator who does not qualify still sees the campaign, with these as the reason."
        >
          <fieldset className="group">
            <legend>Platforms</legend>
            <div className="checks-inline">
              {PLATFORMS.map((platform) => (
                <CheckboxField
                  key={platform}
                  name={`platform-${platform}`}
                  defaultChecked
                  label={PLATFORM_LABELS[platform]}
                />
              ))}
            </div>
          </fieldset>

          <FieldRow>
            <TextField
              label="Minimum account age (days)"
              type="number"
              name="minAccountAgeDays"
              min={0}
              defaultValue={0}
            />
            <TextField
              label="Minimum followers"
              type="number"
              name="minFollowers"
              min={0}
              defaultValue={0}
            />
          </FieldRow>
        </FieldGroup>

        <FieldGroup legend="Dates">
          <FieldRow>
            <TextField label="Starts" type="datetime-local" name="startsAt" />
            <TextField
              label="Ends"
              type="datetime-local"
              name="endsAt"
              hint="Blank for no end date."
            />
          </FieldRow>
        </FieldGroup>

        <FieldGroup
          legend="Source and rules"
          note="What creators cut from, and what a clip must and must not contain. Both are frozen into the terms version creators accept."
        >
          <LinkEditor initial={[]} />
          <TextField
            label="Content rules"
            name="contentRules"
            textarea
            rows={6}
            hint="Shown to creators, word for word, on the campaign card and the submit screen."
          />
          <TextField
            label="Terms text"
            name="termsText"
            textarea
            rows={4}
            hint="Frozen into the terms version. What a creator's acceptance is hashed against."
          />
        </FieldGroup>

        {error && <Alert tone="critical">{error}</Alert>}

        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create as a draft'}
        </Button>
      </form>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Edit one                                                            */
/* ------------------------------------------------------------------ */

export function AdminCampaignDetail(): React.JSX.Element {
  const { idOrSlug = '' } = useParams();
  const [campaign, setCampaign] = useState<AdminCampaign | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const body = await apiGet<{ campaign: AdminCampaign }>(`/admin/campaigns/${idOrSlug}`);
      setCampaign(body.campaign);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the campaign.');
    }
  }, [idOrSlug]);

  useEffect(() => {
    void load();
  }, [load]);

  async function changeStatus(status: CampaignStatus): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await apiSend('POST', `/admin/campaigns/${idOrSlug}/status`, { status });
      setNotice(`This campaign is now ${statusLabel(status, 'campaign').toLowerCase()}.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the status.');
    }
  }

  async function save(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setError(null);
    setNotice(null);
    try {
      await apiSend('PATCH', `/admin/campaigns/${idOrSlug}`, {
        name: String(form.get('name')),
        objective: emptyToNull(form.get('objective')),
        category: String(form.get('category')),
        contentRules: emptyToNull(form.get('contentRules')),
        sourceLinks: readSourceLinks(form),
        joinMode: joinModeOf(form.get('joinMode')),
        endsAt: emptyToNull(form.get('endsAt')),
        brandTermsAcceptedAt: emptyToNull(form.get('brandTermsAcceptedAt')),
        allowedPlatforms: PLATFORMS.filter((p) => form.get(`platform-${p}`) === 'on'),
        ratePerThousandCents: dollarsToCents('Rate', form.get('ratePerThousand')),
        platformRates: platformRatesOf(form),
        budgetCents: dollarsToCents('Budget', form.get('budget')),
        perCreatorCapCents: dollarsToCentsOrNull('Per-creator cap', form.get('perCreatorCap')),
        perSubmissionCapCents: dollarsToCentsOrNull('Per-clip cap', form.get('perSubmissionCap')),
        minAccountAgeDays: wholeNumber('Minimum account age', form.get('minAccountAgeDays')),
        minFollowers: wholeNumber('Minimum followers', form.get('minFollowers')),
      });
      setNotice('Saved. A new terms version was frozen.');
      await load();
    } catch (err) {
      setError(refusal(err) ?? 'Could not save.');
    }
  }

  async function decide(
    creatorId: string,
    action: 'approve' | 'decline',
    reason?: string,
  ): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      const body = await apiSend<{ campaign: AdminCampaign }>(
        'POST',
        `/admin/campaigns/${idOrSlug}/applicants/${creatorId}/${action}`,
        action === 'decline' ? { reason } : {},
      );
      setCampaign(body.campaign);
      setNotice(action === 'approve' ? 'Approved: they can submit clips now.' : 'Declined, with your reason.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record the decision.');
    }
  }

  async function fund(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setError(null);
    setNotice(null);
    try {
      await apiSend('POST', `/admin/campaigns/${idOrSlug}/funding`, {
        amountCents: dollarsToCents('Amount', form.get('amount')),
        serviceFeeCents: dollarsToCents('Service fee', form.get('serviceFee')),
        invoiceOrPaymentReference: String(form.get('invoiceOrPaymentReference')),
        paymentDate: new Date(String(form.get('paymentDate'))).toISOString(),
        evidenceReference: String(form.get('evidenceReference')),
      });
      setNotice('Funding recorded.');
      await load();
    } catch (err) {
      setError(refusal(err) ?? 'Could not record the funding.');
    }
  }

  if (error && !campaign) {
    return (
      <>
        <PageHeader eyebrow="Operator" title="Campaign" />
        <Alert tone="critical" title="Could not load the campaign.">
          <span>{error}</span>
        </Alert>
      </>
    );
  }

  if (!campaign) return <LoadingPage eyebrow="Operator" title="Campaign" stats />;

  const frozen = (['ACTIVE', 'PAUSED', 'COMPLETED', 'CANCELLED'] as CampaignStatus[]).includes(
    campaign.status,
  );
  const shortfall = campaign.budgetCents - campaign.creatorBudgetFundedCents;

  return (
    <>
      <PageHeader
        eyebrow={
          <>
            <Link to="/admin/campaigns">Campaigns</Link> <span aria-hidden="true">·</span>{' '}
            {campaign.brand.displayName}
          </>
        }
        title={campaign.name}
        lead={campaign.objective ?? undefined}
        actions={<StatusPill status={campaign.status} domain="campaign" />}
      />

      {notice && <Alert tone="positive">{notice}</Alert>}
      {error && <Alert tone="critical">{error}</Alert>}

      <StatGrid>
        <Stat
          label="Creator rate per 1,000 views"
          value={<Money cents={campaign.ratePerThousandCents} />}
          note={`${rateSummary(campaign)}Client pays ${formatCents(campaign.clientRatePerThousandCents)}.`}
          emphasis
        />
        <Stat
          label="Budget"
          value={<Money cents={campaign.budgetCents} />}
          note={`${formatCents(campaign.budgetRemainingCents)} left`}
        />
        <Stat
          label="Funded"
          value={<Money cents={campaign.creatorBudgetFundedCents} />}
          note={shortfall > 0 ? `${formatCents(shortfall)} not paid in yet` : 'Fully paid in.'}
        />
        <Stat label="Terms version" value={String(campaign.termsVersion ?? '—')} />
      </StatGrid>

      <Card
        title="How it is going"
        sub="Counted and summed on every read. The views are the same figure the client export and the leaderboard use; every money figure is a sum of ledger entries."
      >
        <StatGrid dense>
          <Stat
            label="Creators joined"
            value={<Num value={campaign.stats.creatorsJoined} />}
            note={`${campaign.stats.creatorsActive} of them have posted.`}
            emphasis={campaign.stats.creatorsJoined > 0}
          />
          <Stat
            label="Clips submitted"
            value={<Num value={campaign.stats.clips.total} />}
            note={clipBreakdown(campaign.stats.clips)}
          />
          <Stat label="Verified views" value={<Num value={campaign.stats.verifiedViews} />} />
          <Stat
            label="Spent"
            value={<Money cents={campaign.stats.spendCents} />}
            note={`${formatCents(campaign.stats.creatorSpendCents)} to creators, ${formatCents(campaign.stats.marginCents)} kept.`}
          />
          <Stat
            label="Budget left"
            value={<Money cents={campaign.stats.budgetRemainingCents} />}
            note={`Of ${formatCents(campaign.stats.fundedCents)} paid in.`}
          />
        </StatGrid>
      </Card>

      {(campaign.joinMode === 'APPLY' || campaign.applicants.length > 0) && (
        <Applicants applicants={campaign.applicants} onDecide={decide} />
      )}

      <Card title="Status" sub="Where this campaign can go from here.">
        <Facts>
          <Fact label="Address">
            <code className="chip quiet">{campaign.slug}</code>
          </Fact>
          <Fact label="Created">
            <When value={campaign.createdAt} />
          </Fact>
          <Fact label="Client accepted terms">
            <When value={campaign.brandTermsAcceptedAt} fallback="Not recorded" />
          </Fact>
          <Fact label="Invoice">
            {campaign.externalInvoiceReference ?? <span className="muted">—</span>}
          </Fact>
          <Fact label="Joining">
            {campaign.joinMode === 'APPLY' ? 'Apply first — you approve each creator' : 'Open'}
          </Fact>
        </Facts>
        <div className="actions">
          {CAMPAIGN_TRANSITIONS[campaign.status].map((next) => (
            <Button
              key={next}
              variant={next === 'ACTIVE' ? 'primary' : next === 'CANCELLED' ? 'danger' : 'ghost'}
              onClick={() => void changeStatus(next)}
            >
              {MOVE_LABELS[next]}
            </Button>
          ))}
          {CAMPAIGN_TRANSITIONS[campaign.status].length === 0 && (
            <span className="muted">This status is terminal — nothing moves from here.</span>
          )}
        </div>
      </Card>

      {/* The clip-library folder this campaign hands its creators. Its own
          card and its own two requests (routes/CampaignLibraryAdmin.tsx); the
          Edit form below knows nothing about it. */}
      <ClipFolderPicker idOrSlug={idOrSlug} />

      <Card title="Edit">
        {frozen && (
          <Alert tone="waiting" title="This campaign has been live">
            <span>
              Rate, caps, budget and the eligibility floors are frozen: creators accepted those
              terms. The rules, links and dates are still editable, and saving freezes a new terms
              version.
            </span>
          </Alert>
        )}
        <form onSubmit={save} className="form">
          <FieldGroup legend="Basics">
            <FieldRow>
              <TextField label="Name" type="text" name="name" defaultValue={campaign.name} required />
              {/* Editable at every status, unlike the economics: a niche is a
                  label on the shelf, not a term anybody accepted, so re-filing a
                  live campaign is neither refused nor a reason to freeze a new
                  terms version. */}
              <SelectField
                label="Category"
                name="category"
                required
                defaultValue={campaign.category}
                hint="The niche creators filter by. Editable even once the campaign is live."
              >
                {CAMPAIGN_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {CAMPAIGN_CATEGORY_LABELS[category]}
                  </option>
                ))}
              </SelectField>
            </FieldRow>
            <TextField
              label="Objective"
              name="objective"
              textarea
              rows={2}
              defaultValue={campaign.objective ?? ''}
            />
            <JoinModeField defaultValue={campaign.joinMode} />
          </FieldGroup>

          <FieldGroup
            legend="Economics"
            note={frozen ? 'Frozen: creators accepted these numbers.' : 'In dollars.'}
          >
            <FieldRow>
              <MoneyField
                label="Creator rate per 1,000 views"
                name="ratePerThousand"
                defaultValue={centsToDollarInput(campaign.ratePerThousandCents)}
                readOnly={frozen}
                hint="What a creator earns per 1,000 verified views, on any platform without its own rate below."
              />
              <MoneyField
                label="Budget"
                name="budget"
                defaultValue={centsToDollarInput(campaign.budgetCents)}
                readOnly={frozen}
                hint="The whole campaign's creator-facing budget."
              />
            </FieldRow>
            <PlatformRateFields rates={overridesOf(campaign)} readOnly={frozen} />
            <FieldRow>
              <MoneyField
                label="Per-creator cap"
                name="perCreatorCap"
                optional
                blankMeans="Blank: no cap — one creator could earn the whole budget."
                defaultValue={centsToDollarInput(campaign.perCreatorCapCents)}
                readOnly={frozen}
                hint="The most any single creator can earn here."
              />
              <MoneyField
                label="Per-clip cap"
                name="perSubmissionCap"
                optional
                blankMeans="Blank: no cap — one clip could earn the whole budget."
                defaultValue={centsToDollarInput(campaign.perSubmissionCapCents)}
                readOnly={frozen}
                hint="The most any single clip can earn, whatever it reaches."
              />
            </FieldRow>
          </FieldGroup>

          <FieldGroup legend="Eligibility">
            <fieldset className="group">
              <legend>Platforms</legend>
              <div className="checks-inline">
                {PLATFORMS.map((platform) => (
                  <CheckboxField
                    key={platform}
                    name={`platform-${platform}`}
                    defaultChecked={campaign.allowedPlatforms.includes(platform as Platform)}
                    label={PLATFORM_LABELS[platform]}
                  />
                ))}
              </div>
            </fieldset>
            <FieldRow>
              <TextField
                label="Minimum account age (days)"
                type="number"
                name="minAccountAgeDays"
                min={0}
                defaultValue={campaign.minAccountAgeDays}
                readOnly={frozen}
              />
              <TextField
                label="Minimum followers"
                type="number"
                name="minFollowers"
                min={0}
                defaultValue={campaign.minFollowers}
                readOnly={frozen}
              />
            </FieldRow>
          </FieldGroup>

          <FieldGroup legend="Dates">
            <FieldRow>
              <TextField
                label="Ends"
                type="datetime-local"
                name="endsAt"
                defaultValue={toLocalInput(campaign.endsAt)}
              />
              <TextField
                label="Client accepted terms on"
                type="datetime-local"
                name="brandTermsAcceptedAt"
                defaultValue={toLocalInput(campaign.brandTermsAcceptedAt)}
              />
            </FieldRow>
          </FieldGroup>

          <FieldGroup legend="Source and rules">
            <LinkEditor initial={campaign.sourceLinks} />
            <TextField
              label="Content rules"
              name="contentRules"
              textarea
              rows={8}
              defaultValue={campaign.contentRules ?? ''}
            />
          </FieldGroup>

          <Button type="submit" variant="primary">
            Save
          </Button>
        </form>
      </Card>

      <Card
        title="Funding"
        sub={`${formatCents(campaign.creatorBudgetFundedCents)} of ${formatCents(campaign.budgetCents)} paid in.`}
        aside={
          shortfall > 0 ? (
            <Badge tone="waiting">{formatCents(shortfall)} short</Badge>
          ) : (
            <Badge tone="positive">Fully funded</Badge>
          )
        }
      >
        <FundingTable campaign={campaign} />

        <form onSubmit={fund} className="form">
          <FieldGroup legend="Record a payment" note="In dollars. Each one needs its evidence.">
            <FieldRow>
              <MoneyField
                label="Amount"
                name="amount"
                required
                hint="What the client actually paid in."
              />
              <MoneyField
                label="Service fee"
                name="serviceFee"
                defaultValue="0"
                hint="The part of it that is not creator budget."
              />
              <TextField label="Payment date" type="date" name="paymentDate" required />
            </FieldRow>
            <FieldRow>
              <TextField
                label="Invoice or payment reference"
                type="text"
                name="invoiceOrPaymentReference"
                required
                hint="A retry with the same reference is refused, not doubled."
              />
              <TextField
                label="Evidence reference"
                type="text"
                name="evidenceReference"
                required
                hint="Where the proof lives: a bank statement line, a receipt."
              />
            </FieldRow>
          </FieldGroup>
          <Button type="submit" variant="primary">
            Record funding
          </Button>
        </form>
      </Card>

      <Card
        title="Terms versions"
        sub="A version is frozen on every save. A creator is bound to the one they accepted, not the latest."
      >
        {campaign.termsVersions.length === 0 ? (
          <EmptyState title="No terms have been frozen yet.">
            Saving this campaign freezes the first version.
          </EmptyState>
        ) : (
          <ul className="rows">
            {campaign.termsVersions.map((version) => (
              <li key={version.id}>
                <div className="row-head">
                  <span className="row-title">Version {version.version}</span>
                  {/* `termsVersion` is the string projection of the same
                      number, so the comparison has to cross that boundary
                      explicitly rather than by coercion. */}
                  {String(version.version) === String(campaign.termsVersion) && (
                    <Badge tone="positive">Current</Badge>
                  )}
                </div>
                <span className="muted">
                  Frozen <When value={version.createdAt} withTime />
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {campaign.sourceLinks.length > 0 && (
        <Card title="Reference materials" sub="What creators are shown: the client's channels, what to cut from, what to read first.">
          <ul className="rows">
            {campaign.sourceLinks.map((link) => (
              <li key={`${link.kind}-${link.url}`}>
                <div className="row-head">
                  <span className="row-title">
                    <ExternalLink href={link.url}>{link.label}</ExternalLink>
                  </span>
                  <Badge plain>{SOURCE_LINK_KIND_LABELS[link.kind]}</Badge>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

function FundingTable({ campaign }: { campaign: AdminCampaign }): React.JSX.Element {
  type FundingRow = AdminCampaign['fundingRecords'][number];
  const columns: Array<Column<FundingRow>> = [
    {
      key: 'reference',
      header: 'Reference',
      primary: true,
      cell: (r) => (
        <>
          {r.invoiceOrPaymentReference}
          <span className="cell-note">{r.evidenceReference}</span>
        </>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      align: 'end',
      cell: (r) => <Money cents={r.amountCents} />,
    },
    {
      key: 'fee',
      header: 'Service fee',
      numeric: true,
      align: 'end',
      cell: (r) => <Money cents={r.serviceFeeCents} />,
    },
    { key: 'paid', header: 'Paid', cell: (r) => <When value={r.paymentDate} /> },
    {
      key: 'ledger',
      header: 'In the ledger',
      cell: (r) =>
        r.ledgerTransactionId ? (
          <Badge tone="positive">Posted</Badge>
        ) : (
          <Badge tone="waiting">Not posted</Badge>
        ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={campaign.fundingRecords}
      getKey={(r) => r.id}
      empty={
        <EmptyState title="No funding recorded yet.">
          A campaign cannot go live until the client's payment is on the books.
        </EmptyState>
      }
    />
  );
}

/**
 * What each transition button says.
 *
 * Not `statusLabel(next).toLowerCase()`: the pill for FUNDED reads "Funded,
 * not live", which is right on a pill and becomes the button "Move to funded,
 * not live". A button is a verb phrase and a pill is a state; they are not the
 * same sentence and cannot be derived from each other.
 */
const MOVE_LABELS: Record<CampaignStatus, string> = {
  DRAFT: 'Move back to draft',
  FUNDED: 'Move to funded',
  ACTIVE: 'Make it live',
  PAUSED: 'Move to paused',
  COMPLETED: 'Move to finished',
  CANCELLED: 'Move to cancelled',
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/**
 * "3 in review · 1 rejected", and nothing at all when there is nothing to say.
 *
 * The parts that are ZERO are left out rather than printed: a note reading
 * "0 in review · 0 rejected · 0 held" on every row of a healthy list is four
 * words of noise per campaign, and the total is already in the column above it.
 */
function clipBreakdown(clips: AdminCampaignStats['clips']): string {
  const parts: string[] = [];
  if (clips.inReview > 0) parts.push(`${clips.inReview} in review`);
  if (clips.approved > 0) parts.push(`${clips.approved} approved`);
  if (clips.rejected > 0) parts.push(`${clips.rejected} rejected`);
  if (clips.held > 0) parts.push(`${clips.held} held`);
  return parts.join(' · ');
}

function emptyToNull(value: FormDataEntryValue | null): string | null {
  const text = String(value ?? '').trim();
  if (!text) return null;
  // A `datetime-local` value has no timezone; the API wants an instant.
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(text) ? new Date(text).toISOString() : text;
}

/**
 * The form's money, read the same way the boxes read it.
 *
 * `MoneyField` has already shown the operator the exact integer under each
 * box; this is the last gate, and it THROWS rather than guessing. The version
 * this replaced ended `Number.isFinite(amount) ? … : 0`, so anything it could
 * not parse was submitted as $0.00 — and the optional cap as "no cap".
 */
class MoneyInputError extends Error {}

/** The sentence to show, when there is one worth showing. */
function refusal(err: unknown): string | null {
  if (err instanceof MoneyInputError) return err.message;
  if (err instanceof ApiError) return err.message;
  return null;
}

function dollarsToCents(label: string, value: FormDataEntryValue | null): number {
  const parsed = parseDollars(String(value ?? ''));
  if (!parsed.ok) throw new MoneyInputError(`${label}: ${parsed.reason}`);
  return parsed.cents;
}

function dollarsToCentsOrNull(label: string, value: FormDataEntryValue | null): number | null {
  const parsed = parseOptionalDollars(String(value ?? ''));
  if (!parsed.ok) throw new MoneyInputError(`${label}: ${parsed.reason}`);
  return parsed.cents;
}

/** A whole number of days or followers. Same rule: no silent zero. */
function wholeNumber(label: string, value: FormDataEntryValue | null): number {
  const text = String(value ?? '').trim();
  if (!/^\d+$/.test(text)) throw new MoneyInputError(`${label}: whole numbers only.`);
  return Number(text);
}

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 16);
}

/** The join-mode select: open, or apply first. */
function JoinModeField({ defaultValue }: { defaultValue: CampaignJoinMode }): React.JSX.Element {
  return (
    <SelectField
      label="Joining"
      name="joinMode"
      defaultValue={defaultValue}
      hint="Open: a creator who qualifies is on the campaign the moment they press Join. Apply first: they apply, and you approve each one from this page."
    >
      {CAMPAIGN_JOIN_MODES.map((mode) => (
        <option key={mode} value={mode}>
          {mode === 'APPLY' ? 'Apply first — I approve each creator' : 'Open to anyone who qualifies'}
        </option>
      ))}
    </SelectField>
  );
}

function joinModeOf(value: FormDataEntryValue | null): CampaignJoinMode {
  return value === 'APPLY' ? 'APPLY' : 'OPEN';
}

/** One dollar box per platform. Blank means "the base rate". */
function PlatformRateFields({
  rates,
  readOnly,
}: {
  rates: PlatformRates;
  readOnly?: boolean;
}): React.JSX.Element {
  return (
    <FieldRow>
      {PLATFORMS.map((platform) => (
        <MoneyField
          key={platform}
          label={`${PLATFORM_LABELS[platform]} rate per 1,000`}
          name={`rate-${platform}`}
          optional
          blankMeans="Blank: the creator rate above."
          defaultValue={centsToDollarInput(rates[platform])}
          readOnly={readOnly}
        />
      ))}
    </FieldRow>
  );
}

/** The per-platform overrides typed into the boxes, blanks left out. */
function platformRatesOf(form: FormData): PlatformRates {
  const out: PlatformRates = {};
  for (const platform of PLATFORMS) {
    const cents = dollarsToCentsOrNull(`${PLATFORM_LABELS[platform]} rate`, form.get(`rate-${platform}`));
    if (cents !== null) out[platform] = cents;
  }
  return out;
}

/**
 * The overrides on a campaign, AS STORED.
 *
 * Not reconstructed from the effective per-platform list: an override equal
 * to the base rate, or on a platform no longer ticked, is invisible there, and
 * a form that rebuilt the overrides from the list sent a different set back on
 * every save — which on a live campaign is a frozen-term change and a 409,
 * with nothing on screen to say why. The API sends the stored set beside the
 * effective one for exactly this box.
 */
function overridesOf(campaign: AdminCampaign): PlatformRates {
  return campaign.platformRateOverrides ?? {};
}

/** "TikTok $1.50 · Instagram $1.00 · " when the platforms differ; nothing when they do not. */
function rateSummary(campaign: AdminCampaign): string {
  const distinct = new Set(campaign.platformRates.map((r) => r.ratePerThousandCents));
  if (distinct.size <= 1) return '';
  return `${campaign.platformRates
    .map((r) => `${PLATFORM_LABELS[r.platform]} ${formatCents(r.ratePerThousandCents)}`)
    .join(' · ')}. `;
}

/**
 * Typed reference links: a row per link with its kind, label and URL.
 *
 * Rows rather than a `label | url` textarea now that a link has a KIND: the
 * client's YouTube channel, a stream recording and a brand document are three
 * different things on the creator's page and the operator has to be able to
 * say which. Pasting a URL guesses the kind from its host; the select wins
 * once touched. Inputs are named by row so the surrounding uncontrolled form
 * reads them back with `readSourceLinks`.
 */
function LinkEditor({ initial }: { initial: SourceLink[] }): React.JSX.Element {
  const [rows, setRows] = useState<Array<SourceLink & { touched: boolean }>>(
    initial.map((link) => ({ ...link, touched: true })),
  );

  const update = (index: number, patch: Partial<SourceLink & { touched: boolean }>): void =>
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <fieldset className="group link-editor">
      <legend>Reference links</legend>
      <p className="muted">
        The client's channels, what to cut from, and anything to read first. Shown to creators
        under "Reference materials", grouped by kind.
      </p>
      {rows.map((row, index) => (
        <div className="link-editor-row" key={index}>
          <SelectField
            label="Kind"
            name={`link-${index}-kind`}
            value={row.kind}
            onChange={(event) =>
              update(index, { kind: event.target.value as SourceLinkKind, touched: true })
            }
          >
            {SOURCE_LINK_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {SOURCE_LINK_KIND_LABELS[kind]}
              </option>
            ))}
          </SelectField>
          <TextField
            label="Label"
            type="text"
            name={`link-${index}-label`}
            value={row.label}
            onChange={(event) => update(index, { label: event.target.value })}
          />
          <TextField
            label="URL"
            type="url"
            name={`link-${index}-url`}
            value={row.url}
            onChange={(event) =>
              update(index, {
                url: event.target.value,
                ...(row.touched ? {} : { kind: guessSourceLinkKind(event.target.value) }),
              })
            }
          />
          <div className="field field-button">
            <Button onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}>
              Remove
            </Button>
          </div>
        </div>
      ))}
      <div className="actions">
        <Button
          onClick={() => setRows((prev) => [...prev, { label: '', url: '', kind: 'OTHER', touched: false }])}
        >
          Add link
        </Button>
      </div>
    </fieldset>
  );
}

/** The editor's rows, read back out of the form. Blank rows are dropped, not half-saved. */
function readSourceLinks(form: FormData): SourceLink[] {
  const out: SourceLink[] = [];
  for (let index = 0; form.has(`link-${index}-url`); index += 1) {
    const url = String(form.get(`link-${index}-url`) ?? '').trim();
    const label = String(form.get(`link-${index}-label`) ?? '').trim();
    const kindRaw = String(form.get(`link-${index}-kind`) ?? 'OTHER');
    const kind = (SOURCE_LINK_KINDS as readonly string[]).includes(kindRaw)
      ? (kindRaw as SourceLinkKind)
      : 'OTHER';
    if (label.length > 0 && /^https?:\/\//i.test(url)) out.push({ label, url, kind });
  }
  return out;
}

/**
 * Who has applied, and the two buttons.
 *
 * Decided applications stay on the list with their decision, because "who did
 * I decline last week and why" is a question the operator gets asked. Declining
 * asks for the sentence the creator will read — there is no decline without
 * one, on this screen or at the API.
 */
function Applicants({
  applicants,
  onDecide,
}: {
  applicants: CampaignApplicant[];
  onDecide: (creatorId: string, action: 'approve' | 'decline', reason?: string) => Promise<void>;
}): React.JSX.Element {
  const pending = applicants.filter((a) => a.applicationStatus === 'PENDING').length;
  return (
    <Card
      title="Applications"
      sub="This campaign is apply-first: nobody can submit a clip until you let them in."
      aside={
        pending > 0 ? (
          <Badge tone="waiting">{pending} waiting</Badge>
        ) : (
          <Badge tone="positive">Nobody waiting</Badge>
        )
      }
    >
      {applicants.length === 0 ? (
        <EmptyState title="Nobody has applied yet.">
          Applications appear here the moment a creator presses Apply on the campaign.
        </EmptyState>
      ) : (
        <ul className="rows" aria-label="Applicants">
          {applicants.map((applicant) => (
            <ApplicantRow key={applicant.creatorId} applicant={applicant} onDecide={onDecide} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ApplicantRow({
  applicant,
  onDecide,
}: {
  applicant: CampaignApplicant;
  onDecide: (creatorId: string, action: 'approve' | 'decline', reason?: string) => Promise<void>;
}): React.JSX.Element {
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const name = applicant.displayName ?? applicant.email;

  const run = async (action: 'approve' | 'decline'): Promise<void> => {
    setBusy(true);
    try {
      await onDecide(applicant.creatorId, action, action === 'decline' ? reason : undefined);
      setDeclining(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li data-application={applicant.applicationStatus}>
      <div className="row-head">
        <span className="row-title">
          {name}
          {applicant.displayName && <span className="cell-note">{applicant.email}</span>}
        </span>
        {applicant.applicationStatus === 'PENDING' ? (
          <Badge tone="waiting">Waiting</Badge>
        ) : applicant.applicationStatus === 'APPROVED' ? (
          <Badge tone="positive">Approved</Badge>
        ) : (
          <Badge tone="critical">Declined</Badge>
        )}
      </div>
      <span className="applicant-accounts">
        {applicant.accounts.length === 0 ? (
          <span>No verified accounts</span>
        ) : (
          applicant.accounts.map((account) => (
            <span key={`${account.platform}-${account.handle}`} className="with-glyph">
              <Glyph
                name={
                  account.platform === 'TIKTOK'
                    ? 'tiktok'
                    : account.platform === 'INSTAGRAM'
                      ? 'instagram'
                      : 'youtube'
                }
                size={14}
              />
              {account.handle}
              {account.followers !== null && ` · ${account.followers.toLocaleString('en-US')} followers`}
            </span>
          ))
        )}
      </span>
      <span className="muted">
        Applied <When value={applicant.appliedAt} withTime fallback="—" />
        {applicant.decidedAt && (
          <>
            {' '}
            · decided <When value={applicant.decidedAt} withTime />
          </>
        )}
        {applicant.declineReason && <> · {applicant.declineReason}</>}
      </span>
      {applicant.applicationStatus === 'PENDING' && (
        <div className="actions">
          <Button variant="primary" disabled={busy} onClick={() => void run('approve')}>
            Approve
          </Button>
          {declining ? (
            <>
              <TextField
                label="Reason for declining"
                type="text"
                name={`decline-${applicant.creatorId}`}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                hint="Shown to the creator, word for word."
              />
              <Button
                variant="danger"
                disabled={busy || reason.trim().length === 0}
                onClick={() => void run('decline')}
              >
                Decline with this reason
              </Button>
              <Button onClick={() => setDeclining(false)}>Cancel</Button>
            </>
          ) : (
            <Button variant="ghost" disabled={busy} onClick={() => setDeclining(true)}>
              Decline…
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
